<template>
  <div>
    <el-table
      v-loading="loading"
      :data="items"
      size="small"
      style="width: 100%"
    >
      <el-table-column type="expand">
        <template #header>
          <el-button
            type="primary"
            size="small"
            icon="RefreshElementIcon"
            :loading="creating"
            :disabled="creating"
            @click="update"
          />
        </template>
        <template #default="scope">
          <div m="4">
            <el-table :data="scope.row.attributes.tasks">
              <el-table-column prop="state" label="Status">
                <template #default="{ row }">
                  <el-tag
                    v-if="row.state === 'in_progress'"
                    type="warning"
                    size="small"
                  >
                    In progress
                  </el-tag>
                  <el-tag
                    v-else-if="row.state === 'done'"
                    type="primary"
                    size="small"
                  >
                    Done
                  </el-tag>
                  <el-tag v-else type="danger" size="small">
                    {{ row.state }}
                  </el-tag>
                </template>
              </el-table-column>
              <el-table-column label="Device" prop="device" />
              <el-table-column label="Country" prop="iso" />
              <el-table-column label="Report" prop="public_report">
                <template #default="{ row }">
                  <el-link
                    v-if="row.public_report"
                    :href="row.public_report"
                    target="_blank"
                  >
                    Report
                  </el-link>
                </template>
              </el-table-column>
              <el-table-column label="Violations" prop="detections" />
              <el-table-column label="Screenshot" prop="screenshot">
                <template #default="{ row }">
                  <el-image
                    style="width: 50px; height: 50px"
                    :src="row.screenshot"
                    :preview-src-list="[row.screenshot]"
                    show-progress
                    fit="cover"
                  />
                </template>
              </el-table-column>
            </el-table>
          </div>
        </template>
      </el-table-column>
      <el-table-column prop="updated_at" label="Date" :formatter="formatter" />
      <el-table-column
        prop="revision"
        label="Revision"
        :formatter="formatter"
      />
      <el-table-column prop="driver" label="Driver" :formatter="formatter" />
      <el-table-column prop="kind" label="Type" :formatter="formatter">
        <template #default="{ row }">
          <el-tag
            v-if="row.attributes.kind === 'manual'"
            type="danger"
            size="small"
          >
            Manual
          </el-tag>
          <el-tag v-else type="success" size="small"> Automatic </el-tag>
        </template>
      </el-table-column>
      <el-table-column
        prop="alerts_count"
        label="Tasks"
        :formatter="formatter"
      />
      <el-table-column prop="state" label="Status">
        <template #default="{ row }">
          <el-tag
            v-if="row.attributes.state === 'in_progress'"
            type="warning"
            size="small"
          >
            In progress
          </el-tag>
          <el-tag
            v-else-if="row.attributes.state === 'done'"
            type="primary"
            size="small"
          >
            Done
          </el-tag>
          <el-tag v-else type="danger" size="small">
            {{ row.attributes.state }}
          </el-tag>
        </template>
      </el-table-column>
      <el-table-column prop="scope_url" label="Report" :width="200">
        <template #header>
          <el-dropdown v-if="withOptions && canRunScan" @command="runScan">
            <el-button
              type="primary"
              size="small"
              icon="PlusElementIcon"
              :loading="creating"
              :disabled="creating"
            >
              Run manual scan
            </el-button>
            <template #dropdown>
              <el-dropdown-menu>
                <el-dropdown-item command="0">Standart</el-dropdown-item>
                <el-dropdown-item command="1">Click URL</el-dropdown-item>
              </el-dropdown-menu>
            </template>
          </el-dropdown>
          <el-button
            v-else-if="canRunScan"
            type="primary"
            size="small"
            icon="PlusElementIcon"
            :loading="creating"
            :disabled="creating"
            @click="runScan"
          >
            Run manual scan
          </el-button>
        </template>
        <template #default="{ row }">
          <el-link
            v-if="row.attributes.scope_url"
            :href="row.attributes.scope_url"
            target="_blank"
          >
            Report
          </el-link>
        </template>
      </el-table-column>
    </el-table>
    <Pagination
      v-if="total > 0"
      class="mt-1"
      :total="total"
      :page="page"
      @pagination="update"
    />
  </div>
</template>

<script lang="ts">
import { defineComponent, computed, watch, onMounted, ref } from 'vue'
import { useAdScansStore } from '@/store/modules/ad_scans'
import { useUserStore } from '@/store/modules/user'
import formatter from '@/utils/table'
import Pagination from '@/components/Pagination.vue'
import { ElMessage } from 'element-plus'
import { safeSubmitProcessing } from '@/utils/helpers'

export default defineComponent({
  name: 'MalwareTable',
  components: { Pagination },
  props: {
    filter: {
      type: Object,
      default: () => {}
    },
    withOptions: {
      type: Boolean,
      default: false
    }
  },
  setup(props) {
    const adScansStore = useAdScansStore()
    const userStore = useUserStore()
    const canRunScan = computed(() => userStore.isStaff)

    const total = computed(() => adScansStore.total)
    const items = computed(() => adScansStore.currentItems)
    const page = computed(() => adScansStore.currentPage)
    const loading = computed(() => adScansStore.listLoading)
    const creating = ref(false)
    const update = ({
      limit = 15,
      newPage = 1
    }: {
      limit?: number
      newPage?: number
    }) => {
      adScansStore.SetFilters(props.filter)
      adScansStore.SetCurrentPage(newPage)
      adScansStore.FetchItems()
    }

    watch(
      () => props.filter,
      (newVal, oldValue) => {
        update({})
        console.log(newVal, oldValue)
      }
    )

    onMounted(() => {
      update({})
    })

    const runScan = async (clickUrlScan = 0) => {
      clickUrlScan = Number(clickUrlScan) > 0

      await safeSubmitProcessing(
        creating,
        () =>
          adScansStore.SaveItem({
            click_url_scan: clickUrlScan,
            ...props.filter
          }),
        (response) => {
          ElMessage.success({
            message:
              'Ad scan has been successfully started. Please check in 5 minutes',
            duration: 6000
          })
          update({})
        },
        (err) => {
          ElMessage.error({
            message: 'Something went wrong',
            duration: 6000
          })
        }
      )
    }

    return {
      update,
      total,
      items,
      loading,
      page,
      formatter,
      runScan,
      creating,
      canRunScan
    }
  }
})
</script>
